import * as i0 from '@angular/core';
import { Directive, Component, ViewEncapsulation, Input, HostBinding, EventEmitter, Output, HostListener, ViewChild, NgModule } from '@angular/core';
import { _VIEW_REPEATER_STRATEGY, _RecycleViewRepeaterStrategy, _DisposeViewRepeaterStrategy, DataSource } from '@angular/cdk/collections';
import * as i1 from '@angular/cdk/table';
import { CdkTable, CDK_TABLE, _COALESCED_STYLE_SCHEDULER, _CoalescedStyleScheduler, STICKY_POSITIONING_LISTENER, CDK_TABLE_TEMPLATE, CdkHeaderCellDef, CdkCellDef, CdkFooterCellDef, CdkColumnDef, CdkHeaderCell, CdkCell, CdkFooterCell, CdkHeaderRowDef, CdkFooterRowDef, CdkRowDef, CdkHeaderRow, CDK_ROW_TEMPLATE, CdkFooterRow, CdkRow, CdkNoDataRow, CdkTextColumn, CdkTableModule } from '@angular/cdk/table';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import * as i2 from '@angular/common';
import { CommonModule } from '@angular/common';
import throttle from 'lodash/throttle';
import { BehaviorSubject, merge, of, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
/**
 * Enables the recycle view repeater strategy, which reduces rendering latency. Not compatible with
 * tables that animate rows.
 */
class SeFeRecycleRowsDirective {
}
SeFeRecycleRowsDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeRecycleRowsDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SeFeRecycleRowsDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: SeFeRecycleRowsDirective, selector: "seFeTable[recycleRows], table[seFeTable][recycleRows]", providers: [
        { provide: _VIEW_REPEATER_STRATEGY, useClass: _RecycleViewRepeaterStrategy },
    ], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeRecycleRowsDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'seFeTable[recycleRows], table[seFeTable][recycleRows]',
                    providers: [
                        { provide: _VIEW_REPEATER_STRATEGY, useClass: _RecycleViewRepeaterStrategy },
                    ],
                }]
        }] });

/* eslint-disable @typescript-eslint/no-use-before-define */
/**
 * Wrapper for the CdkTable with SE tags and styling
 */
class SeFeTableComponent extends CdkTable {
    constructor() {
        super(...arguments);
        this.class = 'se-fe-table';
        /** Overrides the sticky CSS class set by the `CdkTable`. */
        this.stickyCssClass = 'se-fe-table--sticky';
        /** Overrides the need to add position: sticky on every sticky cell element in `CdkTable`. */
        this.needsPositionStickyOnElement = false;
    }
}
SeFeTableComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTableComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
SeFeTableComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeTableComponent, selector: "seFeTable, table[seFeTable]", inputs: { seFeDataFixedLayout: "seFeDataFixedLayout" }, host: { properties: { "class.se-fe-table--fixed-layout": "this.seFeDataFixedLayout", "class": "this.class" } }, providers: [
        { provide: _VIEW_REPEATER_STRATEGY, useClass: _DisposeViewRepeaterStrategy },
        { provide: CdkTable, useExisting: SeFeTableComponent },
        { provide: CDK_TABLE, useExisting: SeFeTableComponent },
        { provide: _COALESCED_STYLE_SCHEDULER, useClass: _CoalescedStyleScheduler },
        // Prevent nested tables from seeing this table's StickyPositioningListener.
        { provide: STICKY_POSITIONING_LISTENER, useValue: null },
    ], usesInheritance: true, ngImport: i0, template: "\n  <ng-content select=\"caption\"></ng-content>\n  <ng-content select=\"colgroup, col\"></ng-content>\n  <ng-container headerRowOutlet></ng-container>\n  <ng-container rowOutlet></ng-container>\n  <ng-container noDataRowOutlet></ng-container>\n  <ng-container footerRowOutlet></ng-container>\n", isInline: true, styles: [".se-fe-table{font:var(--se-small-body-font);color:var(--se-text);background:var(--se-bg)}.se-fe-table thead,.se-fe-table tbody,.se-fe-table tfoot,.se-fe-header-row,.se-fe-row,.se-footer-row,[se-fe-header-row],[se-fe-row],[se-fe-footer-row],.se-fe-table--sticky{background:inherit}.se-fe-row,.se-fe-header-row,.se-fe-footer-row,th.se-fe-header-cell,th.se-fe-cell,td.se-fe-cell,td.se-fe-footer-cell{border-bottom-width:1px;border-bottom-style:solid;border-bottom-color:var(--se-border)}.se-fe-header-cell{color:var(--se-text);font-weight:var(--se-bold)}.se-fe-header-cell.se-fe-table--sticky{position:relative}.se-fe-table-wrapper{display:block;position:relative;overflow:hidden}.se-fe-table-scroll{overflow:auto}table.se-fe-table{border-spacing:0;width:100%}tr.se-fe-header-row{height:56px}tr.se-fe-header-row>th{white-space:nowrap}tr.se-fe-row,tr.se-fe-footer-row{height:54px}th.se-fe-header-cell{text-align:left}th.se-fe-header-cell:not([aria-sort]){padding:var(--se-content-spacing)}th.se-fe-header-cell.se-fe-table--sticky{z-index:5!important}[dir=rtl] th.se-fe-header-cell{text-align:right}th.se-fe-header-cell svg{vertical-align:middle}td.se-fe-cell{z-index:1}td.se-fe-cell,td.se-fe-footer-cell{padding:var(--se-content-spacing-xsmall) var(--se-content-spacing)}[dir=rtl] td.se-fe-cell:first-of-type:not(:only-of-type),[dir=rtl] td.se-fe-footer-cell:first-of-type:not(:only-of-type){padding-left:0;padding-right:16px}[dir=rtl] td.se-fe-cell:last-of-type:not(:only-of-type),[dir=rtl] td.se-fe-footer-cell:last-of-type:not(:only-of-type){padding-right:0;padding-left:16px}.se-fe-table--sticky{position:sticky!important}.se-fe-table--fixed-layout{table-layout:fixed}.se-fe-table-wrapper.se-fe-table--horz-scroll .se-fe-table__shadows-right,.se-fe-table-wrapper.se-fe-table--horz-scroll .se-fe-table__shadows-left{z-index:calc(5 + 2);position:absolute;height:100%;width:10px;top:0;margin:0;opacity:0;transition:opacity .3s}.se-fe-table-wrapper.se-fe-table--vert-scroll .se-fe-table__shadows-top{z-index:calc(5 + 2);position:absolute;height:10px;width:100%;left:0;margin:0;opacity:0;transition:opacity .3s}.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-right-column th.se-fe-header-cell:last-of-type:after,.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-right-column td.se-fe-cell:last-of-type:after,.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-left-column th.se-fe-header-cell:first-of-type:after,.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-left-column td.se-fe-cell:first-of-type:after{content:\"\";opacity:0;transition:opacity .3s;position:absolute;height:100%;width:10px;top:0}.se-fe-table-wrapper.se-fe-table--vert-scroll:not(.scrolled-top) .se-fe-table__shadows-top,.se-fe-table-wrapper.se-fe-table--horz-scroll:not(.scrolled-right):not(.se-fe-table--fixed-right-column) .se-fe-table__shadows-right,.se-fe-table-wrapper.se-fe-table--horz-scroll:not(.scrolled-left):not(.se-fe-table--fixed-left-column) .se-fe-table__shadows-left,.se-fe-table-wrapper.se-fe-table--horz-scroll:not(.scrolled-right).se-fe-table--fixed-right-column th.se-fe-header-cell:last-of-type:after,.se-fe-table-wrapper.se-fe-table--horz-scroll:not(.scrolled-right).se-fe-table--fixed-right-column td.se-fe-cell:last-of-type:after,.se-fe-table-wrapper.se-fe-table--horz-scroll:not(.scrolled-left).se-fe-table--fixed-left-column th.se-fe-header-cell:first-of-type:after,.se-fe-table-wrapper.se-fe-table--horz-scroll:not(.scrolled-left).se-fe-table--fixed-left-column td.se-fe-cell:first-of-type:after{transition:opacity .3s;opacity:1}.se-fe-table-wrapper.se-fe-table--horz-scroll{overflow:hidden;position:relative}.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-left-column th.se-fe-header-cell:first-of-type{z-index:calc(5 + 1)!important}.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-left-column th.se-fe-header-cell:first-of-type,.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-left-column td.se-fe-cell:first-of-type{position:sticky;left:0;padding-left:var(--se-content-spacing);z-index:2}.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-left-column th.se-fe-header-cell:first-of-type:after,.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-left-column td.se-fe-cell:first-of-type:after{background:linear-gradient(to right,var(--se-shadow) 0,var(--se-shadow) -1px,transparent 9px);left:100%}.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-right-column th.se-fe-header-cell:last-of-type{z-index:calc(5 + 1)!important}.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-right-column th.se-fe-header-cell:last-of-type,.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-right-column td.se-fe-cell:last-of-type{position:sticky;right:0;padding-left:var(--se-content-spacing)}.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-right-column th.se-fe-header-cell:last-of-type:after,.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-right-column td.se-fe-cell:last-of-type:after{background:linear-gradient(to left,var(--se-shadow) 0,var(--se-shadow) -1px,transparent 9px);right:100%}.se-fe-table-wrapper.se-fe-table--horz-scroll .se-fe-table__shadows-left{background:linear-gradient(to right,var(--se-shadow) 0,var(--se-shadow) -1px,transparent 9px);left:-1px}.se-fe-table-wrapper.se-fe-table--horz-scroll .se-fe-table__shadows-right{background:linear-gradient(to left,var(--se-shadow) 0,var(--se-shadow) -1px,transparent 9px);right:0}.se-fe-table-wrapper.se-fe-table--vert-scroll{overflow:hidden;position:relative}.se-fe-table-wrapper.se-fe-table--vert-scroll .se-fe-table__shadows-top{background:linear-gradient(to bottom,var(--se-shadow) 0,var(--se-shadow) -1px,transparent 9px);top:56px}\n"], directives: [{ type: i1.HeaderRowOutlet, selector: "[headerRowOutlet]" }, { type: i1.DataRowOutlet, selector: "[rowOutlet]" }, { type: i1.NoDataRowOutlet, selector: "[noDataRowOutlet]" }, { type: i1.FooterRowOutlet, selector: "[footerRowOutlet]" }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTableComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'seFeTable, table[seFeTable]',
                    template: CDK_TABLE_TEMPLATE,
                    styleUrls: ['./se-fe-table.component.scss'],
                    providers: [
                        { provide: _VIEW_REPEATER_STRATEGY, useClass: _DisposeViewRepeaterStrategy },
                        { provide: CdkTable, useExisting: SeFeTableComponent },
                        { provide: CDK_TABLE, useExisting: SeFeTableComponent },
                        { provide: _COALESCED_STYLE_SCHEDULER, useClass: _CoalescedStyleScheduler },
                        // Prevent nested tables from seeing this table's StickyPositioningListener.
                        { provide: STICKY_POSITIONING_LISTENER, useValue: null },
                    ],
                    encapsulation: ViewEncapsulation.None,
                }]
        }], propDecorators: { seFeDataFixedLayout: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.se-fe-table--fixed-layout']
            }], class: [{
                type: HostBinding,
                args: ['class']
            }] } });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
/**
 * Header cell definition for the se-fe-table.
 * Captures the template of a column's header cell and as well as cell-specific properties.
 */
class SeFeHeaderCellDefDirective extends CdkHeaderCellDef {
}
SeFeHeaderCellDefDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeHeaderCellDefDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SeFeHeaderCellDefDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: SeFeHeaderCellDefDirective, selector: "[seFeHeaderCellDef]", providers: [{ provide: CdkHeaderCellDef, useExisting: SeFeHeaderCellDefDirective }], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeHeaderCellDefDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[seFeHeaderCellDef]',
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    providers: [{ provide: CdkHeaderCellDef, useExisting: SeFeHeaderCellDefDirective }],
                }]
        }] });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
/**
 * Cell definition for the se-fe-table.
 * Captures the template of a column's data row cell as well as cell-specific properties.
 */
class SeFeCellDefDirective extends CdkCellDef {
}
SeFeCellDefDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeCellDefDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SeFeCellDefDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: SeFeCellDefDirective, selector: "[seFeCellDef]", providers: [{ provide: CdkCellDef, useExisting: SeFeCellDefDirective }], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeCellDefDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[seFeCellDef]',
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    providers: [{ provide: CdkCellDef, useExisting: SeFeCellDefDirective }],
                }]
        }] });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
/**
 * Footer cell definition for the se-fe-table.
 * Captures the template of a column's footer cell and as well as cell-specific properties.
 */
class SeFeFooterCellDefDirective extends CdkFooterCellDef {
}
SeFeFooterCellDefDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFooterCellDefDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SeFeFooterCellDefDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: SeFeFooterCellDefDirective, selector: "[seFeFooterCellDef]", providers: [{ provide: CdkFooterCellDef, useExisting: SeFeFooterCellDefDirective }], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFooterCellDefDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[seFeFooterCellDef]',
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    providers: [{ provide: CdkFooterCellDef, useExisting: SeFeFooterCellDefDirective }],
                }]
        }] });

/* eslint-disable @typescript-eslint/no-use-before-define */
/**
 * Column definition for the se-fe-table.
 * Defines a set of cells available for a table column.
 */
class SeFeColumnDefDirective extends CdkColumnDef {
    get name() { return this._name; }
    set name(name) { this._setNameInput(name); }
    _updateColumnCssClassName() {
        super._updateColumnCssClassName();
        this._columnCssClassName.push(`se-fe-column-${this.cssClassFriendlyName}`);
    }
}
SeFeColumnDefDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeColumnDefDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SeFeColumnDefDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: SeFeColumnDefDirective, selector: "[seFeColumnDef]", inputs: { name: ["seFeColumnDef", "name"], sticky: ["seFeColumnDefSticky", "sticky"] }, providers: [
        { provide: CdkColumnDef, useExisting: SeFeColumnDefDirective },
        { provide: 'SE_SORT_HEADER_COLUMN_DEF', useExisting: SeFeColumnDefDirective },
    ], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeColumnDefDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[seFeColumnDef]',
                    providers: [
                        { provide: CdkColumnDef, useExisting: SeFeColumnDefDirective },
                        { provide: 'SE_SORT_HEADER_COLUMN_DEF', useExisting: SeFeColumnDefDirective },
                    ],
                }]
        }], propDecorators: { name: [{
                type: Input,
                args: ['seFeColumnDef']
            }], sticky: [{
                type: Input,
                args: ['seFeColumnDefSticky']
            }] } });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
class SeFeHeaderCellDirective extends CdkHeaderCell {
    constructor() {
        super(...arguments);
        this.role = 'columnheader';
        this.class = 'se-fe-header-cell';
    }
}
SeFeHeaderCellDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeHeaderCellDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SeFeHeaderCellDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: SeFeHeaderCellDirective, selector: "seFeHeaderCell, th[seFeHeaderCell]", host: { properties: { "attr.role": "this.role", "class": "this.class" } }, usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeHeaderCellDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'seFeHeaderCell, th[seFeHeaderCell]',
                }]
        }], propDecorators: { role: [{
                type: HostBinding,
                args: ['attr.role']
            }], class: [{
                type: HostBinding,
                args: ['class']
            }] } });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
class SeFeCellDirective extends CdkCell {
    constructor(columnDef, el) {
        super(columnDef, el);
        this.columnDef = columnDef;
        this.el = el;
        this.class = 'se-fe-cell';
    }
    get scopeAttr() {
        return this.isHeader ? 'row' : null;
    }
    get roleAttr() {
        return this.isHeader ? null : 'cell';
    }
    get isHeader() { return this.el.nativeElement.tagName.toLowerCase() === 'th'; }
}
SeFeCellDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeCellDirective, deps: [{ token: i1.CdkColumnDef }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
SeFeCellDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: SeFeCellDirective, selector: "seFeCell, td[seFeCell], th[seFeCell]", host: { properties: { "class": "this.class", "attr.scope": "this.scopeAttr", "attr.role": "this.roleAttr" } }, usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeCellDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'seFeCell, td[seFeCell], th[seFeCell]',
                }]
        }], ctorParameters: function () { return [{ type: i1.CdkColumnDef }, { type: i0.ElementRef }]; }, propDecorators: { class: [{
                type: HostBinding,
                args: ['class']
            }], scopeAttr: [{
                type: HostBinding,
                args: ['attr.scope']
            }], roleAttr: [{
                type: HostBinding,
                args: ['attr.role']
            }] } });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
class SeFeFooterCellDirective extends CdkFooterCell {
    constructor() {
        super(...arguments);
        this.role = 'gridcell';
        this.class = 'se-fe-footer-cell';
    }
}
SeFeFooterCellDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFooterCellDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SeFeFooterCellDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: SeFeFooterCellDirective, selector: "seFeFooterCell, td[seFeFooterCell]", host: { properties: { "attr.role": "this.role", "class": "this.class" } }, usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFooterCellDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'seFeFooterCell, td[seFeFooterCell]',
                }]
        }], propDecorators: { role: [{
                type: HostBinding,
                args: ['attr.role']
            }], class: [{
                type: HostBinding,
                args: ['class']
            }] } });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
/**
 * Header row definition for the se-fe-table.
 * Captures the header row's template and other header properties such as the columns to display.
 */
class SeFeHeaderRowDefDirective extends CdkHeaderRowDef {
    constructor() {
        super(...arguments);
        this.sticky = true;
    }
}
SeFeHeaderRowDefDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeHeaderRowDefDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SeFeHeaderRowDefDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: SeFeHeaderRowDefDirective, selector: "[seFeHeaderRowDef]", inputs: { columns: ["seFeHeaderRowDef", "columns"], sticky: ["seFeHeaderRowDefSticky", "sticky"] }, providers: [{ provide: CdkHeaderRowDef, useExisting: SeFeHeaderRowDefDirective }], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeHeaderRowDefDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[seFeHeaderRowDef]',
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    providers: [{ provide: CdkHeaderRowDef, useExisting: SeFeHeaderRowDefDirective }],
                }]
        }], propDecorators: { columns: [{
                type: Input,
                args: ['seFeHeaderRowDef']
            }], sticky: [{
                type: Input,
                args: ['seFeHeaderRowDefSticky']
            }] } });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
/**
 * Footer row definition for the se-fe-table.
 * Captures the footer row's template and other footer properties such as the columns to display.
 */
class SeFeFooterRowDefDirective extends CdkFooterRowDef {
}
SeFeFooterRowDefDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFooterRowDefDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SeFeFooterRowDefDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: SeFeFooterRowDefDirective, selector: "[seFeFooterRowDef]", inputs: { columns: ["seFeFooterRowDef", "columns"], sticky: ["seFeHeaderRowDefSticky", "sticky"] }, providers: [{ provide: CdkFooterRowDef, useExisting: SeFeFooterRowDefDirective }], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFooterRowDefDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[seFeFooterRowDef]',
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    providers: [{ provide: CdkFooterRowDef, useExisting: SeFeFooterRowDefDirective }],
                }]
        }], propDecorators: { columns: [{
                type: Input,
                args: ['seFeFooterRowDef']
            }], sticky: [{
                type: Input,
                args: ['seFeHeaderRowDefSticky']
            }] } });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
/**
 * Data row definition for the se-fe-table.
 * Captures the data row's template and other properties such as the columns to display and
 * a when predicate that describes when this row should be used.
 */
class SeFeRowDefDirective extends CdkRowDef {
}
SeFeRowDefDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeRowDefDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SeFeRowDefDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: SeFeRowDefDirective, selector: "[seFeRowDef]", inputs: { columns: ["seFeRowDefColumns", "columns"], when: ["seFeRowDefWhen", "when"] }, providers: [{ provide: CdkRowDef, useExisting: SeFeRowDefDirective }], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeRowDefDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[seFeRowDef]',
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    providers: [{ provide: CdkRowDef, useExisting: SeFeRowDefDirective }],
                }]
        }], propDecorators: { columns: [{
                type: Input,
                args: ['seFeRowDefColumns']
            }], when: [{
                type: Input,
                args: ['seFeRowDefWhen']
            }] } });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
/** Header template container that contains the cell outlet. Adds the right class and role. */
class SeFeHeaderRowComponent extends CdkHeaderRow {
    constructor() {
        super(...arguments);
        this.role = 'row';
        this.class = 'se-fe-header-row';
    }
}
SeFeHeaderRowComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeHeaderRowComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
SeFeHeaderRowComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeHeaderRowComponent, selector: "seFeHeaderRow, tr[seFeHeaderRow]", host: { properties: { "attr.role": "this.role", "class": "this.class" } }, providers: [{ provide: CdkHeaderRow, useExisting: SeFeHeaderRowComponent }], usesInheritance: true, ngImport: i0, template: "<ng-container cdkCellOutlet></ng-container>", isInline: true, directives: [{ type: i1.CdkCellOutlet, selector: "[cdkCellOutlet]" }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeHeaderRowComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'seFeHeaderRow, tr[seFeHeaderRow]',
                    template: CDK_ROW_TEMPLATE,
                    encapsulation: ViewEncapsulation.None,
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    providers: [{ provide: CdkHeaderRow, useExisting: SeFeHeaderRowComponent }],
                }]
        }], propDecorators: { role: [{
                type: HostBinding,
                args: ['attr.role']
            }], class: [{
                type: HostBinding,
                args: ['class']
            }] } });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
/** Footer template container that contains the cell outlet. Adds the right class and role. */
class SeFeFooterRowComponent extends CdkFooterRow {
    constructor() {
        super(...arguments);
        this.role = 'row';
        this.class = 'se-fe-footer-row';
    }
}
SeFeFooterRowComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFooterRowComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
SeFeFooterRowComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeFooterRowComponent, selector: "seFeFooterRow, tr[seFeFooterRow]", host: { properties: { "attr.role": "this.role", "class": "this.class" } }, providers: [{ provide: CdkFooterRow, useExisting: SeFeFooterRowComponent }], usesInheritance: true, ngImport: i0, template: "<ng-container cdkCellOutlet></ng-container>", isInline: true, directives: [{ type: i1.CdkCellOutlet, selector: "[cdkCellOutlet]" }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeFooterRowComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'seFeFooterRow, tr[seFeFooterRow]',
                    template: CDK_ROW_TEMPLATE,
                    encapsulation: ViewEncapsulation.None,
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    providers: [{ provide: CdkFooterRow, useExisting: SeFeFooterRowComponent }],
                }]
        }], propDecorators: { role: [{
                type: HostBinding,
                args: ['attr.role']
            }], class: [{
                type: HostBinding,
                args: ['class']
            }] } });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
/** Data row template container that contains the cell outlet. Adds the right class and role. */
class SeFeRowComponent extends CdkRow {
    constructor() {
        super(...arguments);
        this.role = 'row';
        this.class = 'se-fe-row';
        this.seFeRowClick = new EventEmitter();
    }
    get clickableClass() {
        return !!this.seFeRowClick.observers.length;
    }
    onClick(evt) {
        const e = evt.target;
        const selectors = 'a, area, input, select, textarea, button, [tabindex], [contentEditable=true]';
        const preventClick = e.closest(selectors) || e.querySelector(selectors);
        if (!preventClick)
            this.seFeRowClick.emit(this.row);
    }
}
SeFeRowComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeRowComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
SeFeRowComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeRowComponent, selector: "seFeRow, tr[seFeRow]", inputs: { row: ["seFeRow", "row"] }, outputs: { seFeRowClick: "seFeRowClick" }, host: { listeners: { "click": "onClick($event)" }, properties: { "attr.role": "this.role", "class": "this.class", "class.se-fe-theme--can-press": "this.clickableClass" } }, providers: [{ provide: CdkRow, useExisting: SeFeRowComponent }], usesInheritance: true, ngImport: i0, template: "<ng-container cdkCellOutlet></ng-container>", isInline: true, directives: [{ type: i1.CdkCellOutlet, selector: "[cdkCellOutlet]" }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeRowComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'seFeRow, tr[seFeRow]',
                    template: CDK_ROW_TEMPLATE,
                    encapsulation: ViewEncapsulation.None,
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    providers: [{ provide: CdkRow, useExisting: SeFeRowComponent }],
                }]
        }], propDecorators: { role: [{
                type: HostBinding,
                args: ['attr.role']
            }], class: [{
                type: HostBinding,
                args: ['class']
            }], clickableClass: [{
                type: HostBinding,
                args: ['class.se-fe-theme--can-press']
            }], row: [{
                type: Input,
                args: ['seFeRow']
            }], seFeRowClick: [{
                type: Output
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
/** Row that can be used to display a message when no data is shown in the table. */
class SeFeNoDataRowDirective extends CdkNoDataRow {
}
SeFeNoDataRowDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeNoDataRowDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
SeFeNoDataRowDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: SeFeNoDataRowDirective, selector: "ng-template[seFeNoDataRow]", providers: [{ provide: CdkNoDataRow, useExisting: SeFeNoDataRowDirective }], usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeNoDataRowDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'ng-template[seFeNoDataRow]',
                    // eslint-disable-next-line @typescript-eslint/no-use-before-define
                    providers: [{ provide: CdkNoDataRow, useExisting: SeFeNoDataRowDirective }],
                }]
        }] });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
/**
 * Column that simply shows text content for the header and row cells. Assumes that the table
 * is using the native table implementation (`<table>`).
 *
 * By default, the name of this column will be the header text and data property accessor.
 * The header text can be overridden with the `headerText` input. Cell values can be overridden with
 * the `dataAccessor` input. Change the text justification to the start or end using the `justify`
 * input.
 */
class SeFeTextColumnComponent extends CdkTextColumn {
}
SeFeTextColumnComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTextColumnComponent, deps: null, target: i0.ɵɵFactoryTarget.Component });
SeFeTextColumnComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeTextColumnComponent, selector: "seFeTextColumn", usesInheritance: true, ngImport: i0, template: `
     <ng-container seFeColumnDef>
       <th seFeHeaderCell *seFeHeaderCellDef [style.text-align]="justify">
         {{headerText}}
       </th>
       <td seFeCell *seFeCellDef="let data" [style.text-align]="justify">
         {{dataAccessor(data, name)}}
       </td>
     </ng-container>
   `, isInline: true, directives: [{ type: SeFeColumnDefDirective, selector: "[seFeColumnDef]", inputs: ["seFeColumnDef", "seFeColumnDefSticky"] }, { type: SeFeHeaderCellDefDirective, selector: "[seFeHeaderCellDef]" }, { type: SeFeHeaderCellDirective, selector: "seFeHeaderCell, th[seFeHeaderCell]" }, { type: SeFeCellDefDirective, selector: "[seFeCellDef]" }, { type: SeFeCellDirective, selector: "seFeCell, td[seFeCell], th[seFeCell]" }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTextColumnComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'seFeTextColumn',
                    template: `
     <ng-container seFeColumnDef>
       <th seFeHeaderCell *seFeHeaderCellDef [style.text-align]="justify">
         {{headerText}}
       </th>
       <td seFeCell *seFeCellDef="let data" [style.text-align]="justify">
         {{dataAccessor(data, name)}}
       </td>
     </ng-container>
   `,
                    encapsulation: ViewEncapsulation.None,
                }]
        }] });

var SortConst;
(function (SortConst) {
    SortConst["ASC"] = "asc";
    SortConst["DESC"] = "desc";
    SortConst["Ascending"] = "ascending";
    SortConst["Descending"] = "descending";
    SortConst["None"] = "none";
})(SortConst || (SortConst = {}));
var SortClassConst;
(function (SortClassConst) {
    SortClassConst["ClassBase"] = "se-fe-sort--arrow";
    SortClassConst["ClassASC"] = "se-fe-sort--asc";
    SortClassConst["ClassDESC"] = "se-fe-sort--desc";
})(SortClassConst || (SortClassConst = {}));
const KEY_ENTER = 'Enter';

class SeFeTableSortDirective {
    constructor() {
        this.active = null;
        this.direction = null;
        this.seFeSortChange = new EventEmitter();
        //noop
    }
    sort(column) {
        let { direction } = this;
        if (this.active !== column) {
            this.direction = null;
            this.active = column;
        }
        if (this.direction === null) {
            direction = SortConst.ASC;
        }
        else if (this.direction === SortConst.ASC) {
            direction = SortConst.DESC;
        }
        else if (this.direction === SortConst.DESC) {
            direction = null;
        }
        setTimeout(() => this.seFeSortChange.emit({
            column,
            direction,
        }));
        this.direction = direction;
    }
}
SeFeTableSortDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTableSortDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
SeFeTableSortDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: SeFeTableSortDirective, selector: "[seFeTableSort]", outputs: { seFeSortChange: "seFeSortChange" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTableSortDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[seFeTableSort]',
                }]
        }], ctorParameters: function () { return []; }, propDecorators: { seFeSortChange: [{
                type: Output
            }] } });

class SeFeTableSortHeaderComponent {
    constructor(sorter) {
        this.sorter = sorter;
        //noop
    }
    sort() {
        this.sorter.sort(this.ref);
    }
    onEnter(e) {
        if (e.key === KEY_ENTER) {
            this.sorter.sort(this.ref);
        }
    }
    get isActive() {
        var _a, _b;
        return ((_a = this.sorter) === null || _a === void 0 ? void 0 : _a.active) === this.ref && ((_b = this.sorter) === null || _b === void 0 ? void 0 : _b.direction) !== null;
    }
    get ariaSort() {
        var _a, _b;
        if (((_a = this.sorter) === null || _a === void 0 ? void 0 : _a.active) !== this.ref)
            return SortConst.None;
        return ((_b = this.sorter) === null || _b === void 0 ? void 0 : _b.direction) === SortConst.ASC ?
            SortConst.Ascending : SortConst.Descending;
    }
}
SeFeTableSortHeaderComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTableSortHeaderComponent, deps: [{ token: SeFeTableSortDirective }], target: i0.ɵɵFactoryTarget.Component });
SeFeTableSortHeaderComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeTableSortHeaderComponent, selector: "[seFeTableSortHeader]", inputs: { ref: ["seFeDataRef", "ref"] }, host: { listeners: { "click": "sort()", "keyup": "onEnter($event)" }, properties: { "class.se-fe-sort--active": "this.isActive", "attr.aria-sort": "this.ariaSort" } }, ngImport: i0, template: "<div class=\" se-fe-theme--can-focus se-fe-sort__button\" role=\"button\" tabindex=\"0\">\n  <ng-content></ng-content>\n  <div\n    [ngClass]=\"{\n      'se-fe-sort--arrow': true,\n      'se-fe-sort--asc': sorter?.active === ref && sorter?.direction === 'asc',\n      'se-fe-sort--desc': sorter?.active === ref && sorter?.direction === 'desc'}\">\n      <svg \n        *ngIf=\"!(sorter?.active === ref && sorter?.direction)\" \n        xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" height=\"24\" width=\"24\" role=\"img\" aria-hidden=\"true\">\n        <path xmlns=\"http://www.w3.org/2000/svg\" d=\"M8.225 8.325Q7.95 8.05 7.95 7.6Q7.95 7.15 8.225 6.875L11.3 3.8Q11.45 3.65 11.625 3.587Q11.8 3.525 12 3.525Q12.2 3.525 12.388 3.587Q12.575 3.65 12.7 3.8L15.8 6.9Q16.075 7.175 16.062 7.612Q16.05 8.05 15.775 8.325Q15.5 8.6 15.05 8.6Q14.6 8.6 14.325 8.325L12 6L9.65 8.35Q9.375 8.625 8.938 8.613Q8.5 8.6 8.225 8.325ZM12 20.575Q11.8 20.575 11.625 20.5Q11.45 20.425 11.3 20.3L8.225 17.225Q7.95 16.95 7.95 16.5Q7.95 16.05 8.225 15.775Q8.5 15.5 8.95 15.5Q9.4 15.5 9.675 15.775L12 18.1L14.35 15.75Q14.625 15.475 15.062 15.488Q15.5 15.5 15.775 15.775Q16.05 16.05 16.05 16.5Q16.05 16.95 15.775 17.225L12.7 20.3Q12.575 20.425 12.388 20.5Q12.2 20.575 12 20.575Z\"/>\n      </svg>\n      <svg \n        *ngIf=\"sorter?.active === ref && sorter?.direction !== null\"\n        xmlns=\"http://www.w3.org/2000/svg\" viewBox=\"0 0 24 24\" height=\"24\" width=\"24\" role=\"img\" aria-hidden=\"true\">\n        <path class=\"se-fe-sort__caret\" d=\"M12 14.95Q11.8 14.95 11.625 14.887Q11.45 14.825 11.3 14.675L6.675 10.05Q6.4 9.775 6.413 9.362Q6.425 8.95 6.7 8.675Q6.975 8.4 7.4 8.4Q7.825 8.4 8.1 8.675L12 12.575L15.925 8.65Q16.2 8.375 16.613 8.387Q17.025 8.4 17.3 8.675Q17.575 8.95 17.575 9.375Q17.575 9.8 17.3 10.075L12.7 14.675Q12.55 14.825 12.375 14.887Q12.2 14.95 12 14.95Z\"/>\n      </svg>\n  </div>\n</div>\n\n", styles: [":host{cursor:pointer}:host.se-fe-sort--active{position:relative}:host.se-fe-sort--active:before{--se-bg: var(--se-accent);content:\"\";width:100%;height:4px;background:var(--se-bg);position:absolute;bottom:0;left:0}.se-fe-sort__button{display:flex;align-items:center;grid-gap:var(--se-content-spacing);gap:var(--se-content-spacing);margin:var(--se-content-spacing-xsmall);padding:var(--se-content-spacing-small);border-radius:4px;outline:0}.se-fe-sort__button .se-fe-sort--arrow{fill:var(--se-icon)}.se-fe-sort__button .se-fe-sort--arrow.se-fe-sort--asc{transform:rotate(180deg)}.se-fe-sort__button .se-fe-sort--arrow svg{width:20px;height:20px}\n"], directives: [{ type: i2.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { type: i2.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTableSortHeaderComponent, decorators: [{
            type: Component,
            args: [{
                    selector: '[seFeTableSortHeader]',
                    templateUrl: './se-fe-table-sort-header.component.html',
                    styleUrls: ['./se-fe-table-sort-header.component.scss'],
                }]
        }], ctorParameters: function () { return [{ type: SeFeTableSortDirective }]; }, propDecorators: { ref: [{
                type: Input,
                args: ['seFeDataRef']
            }], sort: [{
                type: HostListener,
                args: ['click']
            }], onEnter: [{
                type: HostListener,
                args: ['keyup', ['$event']]
            }], isActive: [{
                type: HostBinding,
                args: ['class.se-fe-sort--active']
            }], ariaSort: [{
                type: HostBinding,
                args: ['attr.aria-sort']
            }] } });

class SeFeTableDirective {
    constructor(el, cdr) {
        this.el = el;
        this.cdr = cdr;
        this.right = false;
        this.left = true;
        this.top = true;
        this.bottom = false;
        this.vertScroll = false;
        this.horizScroll = false;
        this.scrollBarHorzWidth = 0;
        this.scrollBarVertWidth = 0;
        this.update = throttle(() => {
            const { clientHeight, clientWidth, offsetHeight, offsetWidth, scrollHeight, scrollLeft, scrollTop, scrollWidth } = this.element;
            this.vertScroll = scrollHeight > clientHeight;
            this.horizScroll = scrollWidth > clientWidth;
            this.scrollBarHorzWidth = offsetWidth - clientWidth;
            this.scrollBarVertWidth = offsetHeight - clientHeight;
            if (this.vertScroll) {
                if (scrollTop === 0) {
                    this.top = true;
                    this.bottom = false;
                }
                if (scrollTop > 0) {
                    this.top = false;
                    this.bottom = false;
                }
                if (scrollHeight - clientHeight <= Math.ceil(scrollTop)) {
                    this.bottom = true;
                    this.top = false;
                }
            }
            if (this.horizScroll) {
                if (scrollLeft === 0) {
                    this.right = false;
                    this.left = true;
                }
                if (scrollLeft > 0) {
                    this.right = false;
                    this.left = false;
                }
                if (scrollWidth - clientWidth <= Math.ceil(scrollLeft)) {
                    this.right = true;
                    this.left = false;
                }
            }
            this.cdr.detectChanges();
        }, 100);
        //noop
    }
    ngOnInit() {
        setTimeout(this.update);
    }
    get element() {
        return this.el.nativeElement;
    }
}
SeFeTableDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTableDirective, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Directive });
SeFeTableDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.17", type: SeFeTableDirective, selector: "[seFeTableScroll]", host: { listeners: { "scroll": "ngOnInit()", "window:resize": "ngOnInit()" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTableDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[seFeTableScroll]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { ngOnInit: [{
                type: HostListener,
                args: ['scroll']
            }, {
                type: HostListener,
                args: ['window:resize']
            }] } });

class SeFeTableWrapperComponent {
    constructor(el, cdRef) {
        this.el = el;
        this.cdRef = cdRef;
        this.baseClass = true;
        //noop
    }
    get vertScroll() {
        var _a;
        return !!((_a = this.directive) === null || _a === void 0 ? void 0 : _a.vertScroll);
    }
    get horzScroll() {
        var _a;
        return !!((_a = this.directive) === null || _a === void 0 ? void 0 : _a.horizScroll);
    }
    get scrolledLeft() {
        var _a, _b;
        return !!((_a = this.directive) === null || _a === void 0 ? void 0 : _a.left) && !!((_b = this.directive) === null || _b === void 0 ? void 0 : _b.horizScroll);
    }
    get scrolledRight() {
        var _a, _b;
        return !!((_a = this.directive) === null || _a === void 0 ? void 0 : _a.right) && !!((_b = this.directive) === null || _b === void 0 ? void 0 : _b.horizScroll);
    }
    get scrolledTop() {
        var _a, _b;
        return !!((_a = this.directive) === null || _a === void 0 ? void 0 : _a.top) && !!((_b = this.directive) === null || _b === void 0 ? void 0 : _b.vertScroll);
    }
    onResize() {
        this.updateShadows();
    }
    ngAfterViewInit() {
        this.cdRef.detectChanges(); // force change detection so getters on directive don't throw changed after checked errors
        setTimeout(() => {
            this.updateShadows();
            this.cdRef.detectChanges();
        });
    }
    ngOnDestroy() {
        this.cdRef.detach();
    }
    updateShadows() {
        var _a, _b;
        this.shadowPlacementRight = ((_a = this.directive) === null || _a === void 0 ? void 0 : _a.vertScroll) ? `${(_b = this.directive) === null || _b === void 0 ? void 0 : _b.scrollBarHorzWidth}px` : '0';
        this.shadowPlacementTop = this.stickyHeader === null ? '0' : null;
        this.shadowLength = this.adjustShadowWidth;
        this.shadowHeight = this.adjustShadowHeight;
    }
    get adjustShadowWidth() {
        var _a, _b;
        if (!((_a = this.directive) === null || _a === void 0 ? void 0 : _a.vertScroll))
            return null;
        return `calc(100% - ${(_b = this.directive) === null || _b === void 0 ? void 0 : _b.scrollBarHorzWidth}px)`;
    }
    get adjustShadowHeight() {
        var _a, _b;
        if (!((_a = this.directive) === null || _a === void 0 ? void 0 : _a.horizScroll))
            return null;
        return `calc(100% - ${(_b = this.directive) === null || _b === void 0 ? void 0 : _b.scrollBarVertWidth}px)`;
    }
    get stickyHeader() {
        return this.el.nativeElement.querySelector('.se-fe-table--sticky.se-fe-table--sticky-border-elem-top');
    }
}
SeFeTableWrapperComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTableWrapperComponent, deps: [{ token: i0.ElementRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
SeFeTableWrapperComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "12.0.0", version: "12.2.17", type: SeFeTableWrapperComponent, selector: "se-fe-table-wrapper", inputs: { seFeDataFixedLeftColumn: "seFeDataFixedLeftColumn", seFeDataFixedRightColumn: "seFeDataFixedRightColumn" }, host: { listeners: { "window:resize": "onResize()" }, properties: { "class.se-fe-table-wrapper": "this.baseClass", "class.se-fe-table--vert-scroll": "this.vertScroll", "class.se-fe-table--horz-scroll": "this.horzScroll", "class.scrolled-left": "this.scrolledLeft", "class.scrolled-right": "this.scrolledRight", "class.scrolled-top": "this.scrolledTop", "class.se-fe-table--fixed-left-column": "this.seFeDataFixedLeftColumn", "class.se-fe-table--fixed-right-column": "this.seFeDataFixedRightColumn" } }, viewQueries: [{ propertyName: "directive", first: true, predicate: SeFeTableDirective, descendants: true, read: SeFeTableDirective }], ngImport: i0, template: "<div class=\"se-fe-table__shadows-left\" [style.height]=\"shadowHeight\"></div>\n<div [style.right]=\"shadowPlacementRight\" [style.height]=\"shadowHeight\"class=\"se-fe-table__shadows-right\"></div>\n<div [style.top]=\"shadowPlacementTop\" [style.width]=\"shadowLength\" class=\"se-fe-table__shadows-top\"></div>\n<div seFeTableScroll class=\"se-fe-table-scroll\">\n  <ng-content></ng-content>\n</div>\n\n", styles: [".se-fe-table{font:var(--se-small-body-font);color:var(--se-text);background:var(--se-bg)}.se-fe-table thead,.se-fe-table tbody,.se-fe-table tfoot,.se-fe-header-row,.se-fe-row,.se-footer-row,[se-fe-header-row],[se-fe-row],[se-fe-footer-row],.se-fe-table--sticky{background:inherit}.se-fe-row,.se-fe-header-row,.se-fe-footer-row,th.se-fe-header-cell,th.se-fe-cell,td.se-fe-cell,td.se-fe-footer-cell{border-bottom-width:1px;border-bottom-style:solid;border-bottom-color:var(--se-border)}.se-fe-header-cell{color:var(--se-text);font-weight:var(--se-bold)}.se-fe-header-cell.se-fe-table--sticky{position:relative}.se-fe-table-wrapper{display:block;position:relative;overflow:hidden}.se-fe-table-scroll{overflow:auto}table.se-fe-table{border-spacing:0;width:100%}tr.se-fe-header-row{height:56px}tr.se-fe-header-row>th{white-space:nowrap}tr.se-fe-row,tr.se-fe-footer-row{height:54px}th.se-fe-header-cell{text-align:left}th.se-fe-header-cell:not([aria-sort]){padding:var(--se-content-spacing)}th.se-fe-header-cell.se-fe-table--sticky{z-index:5!important}[dir=rtl] th.se-fe-header-cell{text-align:right}th.se-fe-header-cell svg{vertical-align:middle}td.se-fe-cell{z-index:1}td.se-fe-cell,td.se-fe-footer-cell{padding:var(--se-content-spacing-xsmall) var(--se-content-spacing)}[dir=rtl] td.se-fe-cell:first-of-type:not(:only-of-type),[dir=rtl] td.se-fe-footer-cell:first-of-type:not(:only-of-type){padding-left:0;padding-right:16px}[dir=rtl] td.se-fe-cell:last-of-type:not(:only-of-type),[dir=rtl] td.se-fe-footer-cell:last-of-type:not(:only-of-type){padding-right:0;padding-left:16px}.se-fe-table--sticky{position:sticky!important}.se-fe-table--fixed-layout{table-layout:fixed}.se-fe-table-wrapper.se-fe-table--horz-scroll .se-fe-table__shadows-right,.se-fe-table-wrapper.se-fe-table--horz-scroll .se-fe-table__shadows-left{z-index:calc(5 + 2);position:absolute;height:100%;width:10px;top:0;margin:0;opacity:0;transition:opacity .3s}.se-fe-table-wrapper.se-fe-table--vert-scroll .se-fe-table__shadows-top{z-index:calc(5 + 2);position:absolute;height:10px;width:100%;left:0;margin:0;opacity:0;transition:opacity .3s}.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-right-column th.se-fe-header-cell:last-of-type:after,.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-right-column td.se-fe-cell:last-of-type:after,.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-left-column th.se-fe-header-cell:first-of-type:after,.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-left-column td.se-fe-cell:first-of-type:after{content:\"\";opacity:0;transition:opacity .3s;position:absolute;height:100%;width:10px;top:0}.se-fe-table-wrapper.se-fe-table--vert-scroll:not(.scrolled-top) .se-fe-table__shadows-top,.se-fe-table-wrapper.se-fe-table--horz-scroll:not(.scrolled-right):not(.se-fe-table--fixed-right-column) .se-fe-table__shadows-right,.se-fe-table-wrapper.se-fe-table--horz-scroll:not(.scrolled-left):not(.se-fe-table--fixed-left-column) .se-fe-table__shadows-left,.se-fe-table-wrapper.se-fe-table--horz-scroll:not(.scrolled-right).se-fe-table--fixed-right-column th.se-fe-header-cell:last-of-type:after,.se-fe-table-wrapper.se-fe-table--horz-scroll:not(.scrolled-right).se-fe-table--fixed-right-column td.se-fe-cell:last-of-type:after,.se-fe-table-wrapper.se-fe-table--horz-scroll:not(.scrolled-left).se-fe-table--fixed-left-column th.se-fe-header-cell:first-of-type:after,.se-fe-table-wrapper.se-fe-table--horz-scroll:not(.scrolled-left).se-fe-table--fixed-left-column td.se-fe-cell:first-of-type:after{transition:opacity .3s;opacity:1}.se-fe-table-wrapper.se-fe-table--horz-scroll{overflow:hidden;position:relative}.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-left-column th.se-fe-header-cell:first-of-type{z-index:calc(5 + 1)!important}.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-left-column th.se-fe-header-cell:first-of-type,.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-left-column td.se-fe-cell:first-of-type{position:sticky;left:0;padding-left:var(--se-content-spacing);z-index:2}.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-left-column th.se-fe-header-cell:first-of-type:after,.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-left-column td.se-fe-cell:first-of-type:after{background:linear-gradient(to right,var(--se-shadow) 0,var(--se-shadow) -1px,transparent 9px);left:100%}.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-right-column th.se-fe-header-cell:last-of-type{z-index:calc(5 + 1)!important}.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-right-column th.se-fe-header-cell:last-of-type,.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-right-column td.se-fe-cell:last-of-type{position:sticky;right:0;padding-left:var(--se-content-spacing)}.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-right-column th.se-fe-header-cell:last-of-type:after,.se-fe-table-wrapper.se-fe-table--horz-scroll.se-fe-table--fixed-right-column td.se-fe-cell:last-of-type:after{background:linear-gradient(to left,var(--se-shadow) 0,var(--se-shadow) -1px,transparent 9px);right:100%}.se-fe-table-wrapper.se-fe-table--horz-scroll .se-fe-table__shadows-left{background:linear-gradient(to right,var(--se-shadow) 0,var(--se-shadow) -1px,transparent 9px);left:-1px}.se-fe-table-wrapper.se-fe-table--horz-scroll .se-fe-table__shadows-right{background:linear-gradient(to left,var(--se-shadow) 0,var(--se-shadow) -1px,transparent 9px);right:0}.se-fe-table-wrapper.se-fe-table--vert-scroll{overflow:hidden;position:relative}.se-fe-table-wrapper.se-fe-table--vert-scroll .se-fe-table__shadows-top{background:linear-gradient(to bottom,var(--se-shadow) 0,var(--se-shadow) -1px,transparent 9px);top:56px}\n"], directives: [{ type: SeFeTableDirective, selector: "[seFeTableScroll]" }], encapsulation: i0.ViewEncapsulation.None });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTableWrapperComponent, decorators: [{
            type: Component,
            args: [{
                    selector: 'se-fe-table-wrapper',
                    templateUrl: './se-fe-table-wrapper.component.html',
                    styleUrls: ['../se-fe-table.component.scss'],
                    encapsulation: ViewEncapsulation.None,
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { baseClass: [{
                type: HostBinding,
                args: ['class.se-fe-table-wrapper']
            }], directive: [{
                type: ViewChild,
                args: [SeFeTableDirective, { read: SeFeTableDirective }]
            }], vertScroll: [{
                type: HostBinding,
                args: ['class.se-fe-table--vert-scroll']
            }], horzScroll: [{
                type: HostBinding,
                args: ['class.se-fe-table--horz-scroll']
            }], scrolledLeft: [{
                type: HostBinding,
                args: ['class.scrolled-left']
            }], scrolledRight: [{
                type: HostBinding,
                args: ['class.scrolled-right']
            }], scrolledTop: [{
                type: HostBinding,
                args: ['class.scrolled-top']
            }], seFeDataFixedLeftColumn: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.se-fe-table--fixed-left-column']
            }], seFeDataFixedRightColumn: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.se-fe-table--fixed-right-column']
            }], onResize: [{
                type: HostListener,
                args: ['window:resize']
            }] } });

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
const EXPORTED_DECLARATIONS = [
    // Table
    SeFeTableComponent,
    SeFeRecycleRowsDirective,
    SeFeTableDirective,
    SeFeTableWrapperComponent,
    // Template defs
    SeFeHeaderCellDefDirective,
    SeFeHeaderRowDefDirective,
    SeFeColumnDefDirective,
    SeFeCellDefDirective,
    SeFeRowDefDirective,
    SeFeFooterCellDefDirective,
    SeFeFooterRowDefDirective,
    // Cell directives
    SeFeHeaderCellDirective,
    SeFeCellDirective,
    SeFeFooterCellDirective,
    // Row components
    SeFeHeaderRowComponent,
    SeFeRowComponent,
    SeFeFooterRowComponent,
    // Row directives
    SeFeNoDataRowDirective,
    // Column component
    SeFeTextColumnComponent,
    // Sort
    SeFeTableSortHeaderComponent,
    SeFeTableSortDirective,
];
class SeFeTableModule {
}
SeFeTableModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTableModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SeFeTableModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTableModule, declarations: [
        // Table
        SeFeTableComponent,
        SeFeRecycleRowsDirective,
        SeFeTableDirective,
        SeFeTableWrapperComponent,
        // Template defs
        SeFeHeaderCellDefDirective,
        SeFeHeaderRowDefDirective,
        SeFeColumnDefDirective,
        SeFeCellDefDirective,
        SeFeRowDefDirective,
        SeFeFooterCellDefDirective,
        SeFeFooterRowDefDirective,
        // Cell directives
        SeFeHeaderCellDirective,
        SeFeCellDirective,
        SeFeFooterCellDirective,
        // Row components
        SeFeHeaderRowComponent,
        SeFeRowComponent,
        SeFeFooterRowComponent,
        // Row directives
        SeFeNoDataRowDirective,
        // Column component
        SeFeTextColumnComponent,
        // Sort
        SeFeTableSortHeaderComponent,
        SeFeTableSortDirective], imports: [CdkTableModule,
        FormsModule,
        ReactiveFormsModule,
        CommonModule], exports: [
        // Table
        SeFeTableComponent,
        SeFeRecycleRowsDirective,
        SeFeTableDirective,
        SeFeTableWrapperComponent,
        // Template defs
        SeFeHeaderCellDefDirective,
        SeFeHeaderRowDefDirective,
        SeFeColumnDefDirective,
        SeFeCellDefDirective,
        SeFeRowDefDirective,
        SeFeFooterCellDefDirective,
        SeFeFooterRowDefDirective,
        // Cell directives
        SeFeHeaderCellDirective,
        SeFeCellDirective,
        SeFeFooterCellDirective,
        // Row components
        SeFeHeaderRowComponent,
        SeFeRowComponent,
        SeFeFooterRowComponent,
        // Row directives
        SeFeNoDataRowDirective,
        // Column component
        SeFeTextColumnComponent,
        // Sort
        SeFeTableSortHeaderComponent,
        SeFeTableSortDirective] });
SeFeTableModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTableModule, imports: [[
            CdkTableModule,
            FormsModule,
            ReactiveFormsModule,
            CommonModule,
        ]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.17", ngImport: i0, type: SeFeTableModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CdkTableModule,
                        FormsModule,
                        ReactiveFormsModule,
                        CommonModule,
                    ],
                    exports: [EXPORTED_DECLARATIONS],
                    declarations: EXPORTED_DECLARATIONS,
                }]
        }] });

const MAX_SAFE_INTEGER = 9007199254740991;
class SeFeTableDataSource extends DataSource {
    constructor(initialData = [], sort) {
        super();
        this.renderChangesSubscription = null;
        this.renderData = new BehaviorSubject([]);
        this._data = new BehaviorSubject(initialData);
        this._sort = new BehaviorSubject(sort);
        this.sort = sort;
        if (this.sort) {
            this.sort.seFeSortChange.next(undefined);
        }
        this.updateChangeSubscription();
    }
    get data() {
        return this._data.value;
    }
    set data(data) {
        this._data.next(data);
    }
    get sort() {
        return this._sortDirective;
    }
    set sort(sort) {
        this._sortDirective = sort;
        this.updateChangeSubscription();
    }
    /**
     * Connect this data source to the table. The table will only update when
     * the returned stream emits new items.
     * @returns A stream of the items to be rendered.
     */
    connect() {
        if (!this.renderChangesSubscription) {
            this.updateChangeSubscription();
        }
        return this.renderData;
    }
    /**
     *  Called when the table is being destroyed. Use this function, to clean up
     * any open connections or free any held resources that were set up during connect.
     */
    disconnect() {
        var _a;
        (_a = this.renderChangesSubscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        this.renderChangesSubscription = null;
    }
    orderData(data) {
        if (!this._sortDirective) {
            return data;
        }
        return this.sortData(data.slice(), this._sortDirective);
    }
    sortData(data, sorterDir) {
        const { active, direction } = sorterDir;
        if (!active || direction === '')
            return data;
        const sort = (a, b) => {
            const mod = direction === 'asc' ? 1 : -1;
            const props = active.trim().replace('[', '.').replace(']', '').split(/\s*,\s*/g); // ['prop', 'nested.prop', 'arr[1].prop']
            while (props.length) {
                const prop = props.shift();
                const sort = this.compare(this.getProp(a, prop), this.getProp(b, prop)) * mod;
                if (sort || !props.length)
                    return sort;
            }
        };
        return data.sort(sort);
    }
    updateChangeSubscription() {
        var _a;
        const sortChange = this.sort ? merge(this._sortDirective.seFeSortChange) : of(null);
        const orderedData = combineLatest([this._data, sortChange]).pipe(map(([data]) => this.orderData(data)));
        (_a = this.renderChangesSubscription) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        this.renderChangesSubscription = orderedData.subscribe(data => this.renderData.next(data));
    }
    getProp(obj, prop) {
        return prop.split('.').reduce((o, p) => o[p], obj);
    }
    compare(a, b) {
        if (!this.defined(a) && !this.defined(b))
            return 0;
        if (!this.defined(a))
            return 1;
        if (!this.defined(b))
            return -1;
        if (a === b)
            return 0;
        if (!isNaN(a) && !isNaN(b))
            return Number(a) - Number(b);
        return a > b ? 1 : -1;
    }
    defined(a) {
        return a !== null && a !== undefined;
    }
}

/**
 * Generated bundle index. Do not edit.
 */

export { SeFeCellDefDirective, SeFeCellDirective, SeFeColumnDefDirective, SeFeFooterCellDefDirective, SeFeFooterCellDirective, SeFeFooterRowComponent, SeFeFooterRowDefDirective, SeFeHeaderCellDefDirective, SeFeHeaderCellDirective, SeFeHeaderRowComponent, SeFeHeaderRowDefDirective, SeFeNoDataRowDirective, SeFeRecycleRowsDirective, SeFeRowComponent, SeFeRowDefDirective, SeFeTableComponent, SeFeTableDataSource, SeFeTableDirective, SeFeTableModule, SeFeTableSortDirective, SeFeTableSortHeaderComponent, SeFeTableWrapperComponent, SeFeTextColumnComponent };
